import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { HashLink } from "react-router-hash-link";
import HeroModel from "./HeroModel";
import agenda from "../images/agenda.pdf";

const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  background-color: var(--background);
  transition: background-color 0.2s ease;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HeroLeft = styled.div`
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const HeroRight = styled.div`
  position: relative;
  height: 500px;

  @media (max-width: 768px) {
    height: 300px;
    margin-top: -50px;
    order: -1;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  opacity: 0;
  transform: translateY(30px);
  color: var(--text);
  transition: color 0.2s ease;

  span {
    color: var(--primary);
    position: relative;
    display: inline-block;
    transition: color 0.2s ease;

    &:after {
      content: "";
      position: absolute;
      bottom: 5px;
      left: 0;
      width: 100%;
      height: 8px;
      background: linear-gradient(90deg, var(--primary), var(--accent));
      z-index: -1;
      opacity: 0.3;
      transition: background 0.2s ease;
    }
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  color: var(--text-secondary);
  opacity: 0;
  transform: translateY(20px);
  transition: color 0.2s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(20px);

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(HashLink)`
  background: linear-gradient(90deg, var(--primary), var(--accent));
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(108, 99, 255, 0.3);

    &:before {
      left: 100%;
    }
  }
`;

const SecondaryButton = styled(HashLink)`
  background-color: ${(props) =>
    props.theme.colors.background === "#FFFFFF"
      ? "rgba(108, 99, 255, 0.1)"
      : "transparent"};
  color: var(--text);
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  border: 1px solid
    ${(props) =>
      props.theme.colors.background === "#FFFFFF"
        ? "var(--primary)"
        : "rgba(255, 255, 255, 0.2)"};
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary);
    background-color: ${(props) =>
      props.theme.colors.background === "#FFFFFF"
        ? "rgba(108, 99, 255, 0.15)"
        : "rgba(108, 99, 255, 0.1)"};
    transform: translateY(-3px);
  }
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  opacity: 0;
  transform: translateY(20px);
`;

const EventDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  transition: color 0.2s ease;

  svg {
    color: var(--primary);
    transition: color 0.2s ease;
  }
`;

// Animirane čestice u pozadini
const ParticlesContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
`;

const Particle = styled.div`
  position: absolute;
  background: radial-gradient(circle, var(--primary) 0%, transparent 70%);
  border-radius: 50%;
  opacity: 0.3;
`;

function Hero() {
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const buttonsRef = useRef(null);
  const detailsRef = useRef(null);

  // Pojednostavljena animacija bez GSAP-a
  useEffect(() => {
    // Dodajte CSS klase za animaciju umesto GSAP-a
    if (titleRef.current) titleRef.current.style.opacity = "1";
    if (titleRef.current) titleRef.current.style.transform = "translateY(0)";

    if (subtitleRef.current) subtitleRef.current.style.opacity = "1";
    if (subtitleRef.current)
      subtitleRef.current.style.transform = "translateY(0)";

    if (buttonsRef.current) buttonsRef.current.style.opacity = "1";
    if (buttonsRef.current)
      buttonsRef.current.style.transform = "translateY(0)";

    if (detailsRef.current) detailsRef.current.style.opacity = "1";
    if (detailsRef.current)
      detailsRef.current.style.transform = "translateY(0)";

    // Kreiranje čestica bez GSAP-a
    const container = document.createElement("div");
    container.className = "particles-container";
    container.style.position = "absolute";
    container.style.top = "0";
    container.style.left = "0";
    container.style.width = "100%";
    container.style.height = "100%";
    container.style.overflow = "hidden";
    container.style.zIndex = "1";

    const homeElement = document.getElementById("pocetna");
    if (homeElement) {
      homeElement.appendChild(container);
    }

    return () => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };
  }, []);
  const handleOpenPDF = () => {
    window.open(agenda, "_blank");
  };

  return (
    <HeroSection id="pocetna">
      <HeroContent>
        <HeroLeft>
          <HeroTitle ref={titleRef}>
            Pokažite svoje veštine rešavanja problema.{" "}
            <span>POBEDITE NA HAKATONU</span>
          </HeroTitle>
          {/* <HeroSubtitle ref={subtitleRef}>
            Pridružite se UniHub radionicama za programere različitih oblasti -
            frontend, backend, i robotika. Učite od iskusnih predavača i
            povezujte se sa drugim razvojnim inženjerima.
          </HeroSubtitle> */}
          <ButtonGroup ref={buttonsRef}>
            <PrimaryButton
              onClick={handleOpenPDF}
              // to="https://docs.google.com/forms/d/e/1FAIpQLSezOcMfLSszThuY6JAKwXA6ALvLxI1abH3hgyc0poeX_tk_PQ/viewform"
              // target="_blank"
              rel="noopener noreferrer">
              AGENDA
            </PrimaryButton>
            <SecondaryButton to="#o-nama">Saznaj više</SecondaryButton>
          </ButtonGroup>
          <EventDetails ref={detailsRef}>
            <EventDetail>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 2V6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 2V6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3 10H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>25. Aprila 2025. godine</span>
            </EventDetail>
            <EventDetail>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Univerzitet u Novom Pazaru</span>
            </EventDetail>
          </EventDetails>
        </HeroLeft>
        <HeroRight>
          <HeroModel />
        </HeroRight>
      </HeroContent>
    </HeroSection>
  );
}

export default Hero;
